import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { useParams } from "react-router-dom";
import { useSnapshot } from "valtio";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from "../../../../../components/drawer";
import { useGetLead } from "../../../../../services";
import { homeState } from "../../../../../state/home";
import {
  useGetLeadMatrix,
  useGetLeadProfileMatrix,
} from "../../../../../services/organtization/lead/matrix";
import type { Criterion, MatrixTemplate, ResponseField } from "../../../../../types/matrix";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const groupCriteria = (criteria: Criterion[]) => {
  const order: string[] = [];
  const groups: Record<string, Criterion[]> = {};
  for (const c of criteria) {
    const key = c.group_label || "";
    if (!groups[key]) {
      order.push(key);
      groups[key] = [];
    }
    groups[key].push(c);
  }
  return order.map((key) => ({ label: key, criteria: groups[key] }));
};

// ---------------------------------------------------------------------------
// Single response field value — inline, selectable
// ---------------------------------------------------------------------------

const FieldValue = ({ field }: { field: ResponseField }) => {
  const { value, type } = field;
  if (value === null || value === undefined) return null;

  if (type === "boolean") {
    return value ? (
      <span className="text-green-600 font-semibold select-text">Yes</span>
    ) : (
      <span className="text-red-400 font-semibold select-text">No</span>
    );
  }
  // years or text
  return <span className="select-text text-gray-800">{String(value)}</span>;
};

const hasMeaningfulValue = (f: ResponseField) => {
  if (f.value === null || f.value === undefined) return false;
  if (f.type === "years" && (f.value === 0 || f.value === "0")) return false;
  if (f.type === "text" && f.value === "") return false;
  return true;
};

// All response fields for a criterion rendered in one cell — values only, no labels.
const ResponseCell = ({ fields }: { fields: ResponseField[] }) => {
  const meaningful = fields.filter(hasMeaningfulValue);

  if (meaningful.length === 0) {
    return <span className="text-gray-300 select-text">—</span>;
  }

  return (
    <div className="flex flex-col gap-1">
      {meaningful.map((field, i) => (
        <div key={i} className="text-sm">
          <FieldValue field={field} />
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// One group → table with Criterion | Response columns
// ---------------------------------------------------------------------------

const MatrixGroupTable = ({
  label,
  criteria,
  responseHeader,
}: {
  label: string;
  criteria: Criterion[];
  responseHeader: string;
}) => (
  <div className="flex flex-col gap-2">
    {label && (
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-1">
        {label}
      </p>
    )}

    <table className="w-full text-sm border-collapse border border-gray-200 rounded-xl overflow-hidden">
      <thead>
        <tr className="bg-gray-50">
          <th className="text-left text-xs font-medium text-gray-500 px-4 py-2 border-b border-gray-200 w-2/3">
            Criterion
          </th>
          <th className="text-left text-xs font-medium text-gray-500 px-4 py-2 border-b border-l border-gray-200 w-1/3">
            {responseHeader}
          </th>
        </tr>
      </thead>
      <tbody>
        {criteria.map((criterion, i) => (
          <tr
            key={i}
            className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50/60 transition-colors"
          >
            <td className="px-4 py-3 text-gray-800 align-top select-text leading-snug">
              {criterion.text}
            </td>
            <td className="px-4 py-3 align-top border-l border-gray-100">
              <ResponseCell fields={criterion.response_fields} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ---------------------------------------------------------------------------
// Full matrix
// ---------------------------------------------------------------------------

const MatrixGrid = ({ matrix }: { matrix: MatrixTemplate }) => {
  const groups = groupCriteria(matrix.criteria);
  // column_headers[1] is the response column header from the original document.
  // Fall back to joining field labels if not available (old parsed data).
  const responseHeader =
    matrix.column_headers?.[1] ??
    (() => {
      const seen = new Set<string>();
      const labels: string[] = [];
      for (const c of matrix.criteria) {
        for (const f of c.response_fields) {
          if (!seen.has(f.label)) { seen.add(f.label); labels.push(f.label); }
        }
      }
      return labels.join(" · ");
    })();

  return (
    <div className="flex flex-col gap-8 px-6 py-5">
      {matrix.title && (
        <h3 className="font-semibold text-base text-gray-800">{matrix.title}</h3>
      )}
      {groups.map(({ label, criteria }) => (
        <MatrixGroupTable
          key={label}
          label={label}
          criteria={criteria}
          responseHeader={responseHeader}
        />
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Data fetching
// ---------------------------------------------------------------------------

const MatrixContent = ({
  organizationId,
  leadId,
}: {
  organizationId: string;
  leadId: string;
}) => {
  const { data: lead } = useGetLead(
    { leadID: leadId, organizationID: organizationId },
    { enabled: !!leadId },
  );

  const isChild = !!lead?.parentLeadId;
  const parentLeadId = lead?.parentLeadId ?? leadId;
  const profileLinkId = lead?.profileLinkId ?? "";

  const { data: templateMatrix, isLoading: loadingTemplate } = useGetLeadMatrix(
    organizationId,
    parentLeadId,
  );
  const { data: filledMatrix, isLoading: loadingFilled } =
    useGetLeadProfileMatrix(organizationId, parentLeadId, profileLinkId);

  const isLoading = loadingTemplate || loadingFilled;
  const matrix = isChild ? filledMatrix ?? templateMatrix : templateMatrix;

  if (isLoading) {
    return (
      <div className="flex justify-center py-8 text-sm text-gray-500">
        Loading...
      </div>
    );
  }
  if (!matrix) {
    return (
      <div className="px-6 py-4 text-sm text-gray-500">
        No competence matrix found for this assignment.
      </div>
    );
  }

  return <MatrixGrid matrix={matrix} />;
};

// ---------------------------------------------------------------------------
// Drawer shell
// ---------------------------------------------------------------------------

export const MatrixDrawer = () => {
  const { organizationId } = useParams();
  if (!organizationId) throw new Error("Organization ID is required");

  const { matrixDrawerLeadId } = useSnapshot(homeState);

  const handleClose = () => {
    homeState.matrixDrawerLeadId = null;
  };

  return (
    <Drawer
      direction="right"
      open={!!matrixDrawerLeadId}
      onClose={handleClose}
      shouldScaleBackground={false}
    >
      <DrawerContent
        className="min-w-[580px] max-w-[720px]"
        direction="right"
        data-vaul-no-drag
      >
        <VisuallyHidden.Root>
          <DrawerTitle>Competence matrix</DrawerTitle>
          <DrawerDescription>Competence matrix details</DrawerDescription>
        </VisuallyHidden.Root>

        <div className="flex flex-col h-full overflow-y-auto">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
            <h2 className="font-bold text-lg">Competence matrix</h2>
            <button
              type="button"
              onClick={handleClose}
              className="text-sm text-gray-500 hover:text-black"
            >
              Close
            </button>
          </div>

          {matrixDrawerLeadId && (
            <MatrixContent
              organizationId={organizationId}
              leadId={matrixDrawerLeadId}
            />
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
};
